import { apiClient } from './api'

// ===== TYPES =====

export interface DealershipGroupResponse {
  id: string
  groupId: string
  configurationData: {
    groupName: string
    groupDescription?: string
    headquarters?: {
      address?: {
        city?: string
        state?: string
      }
      primaryPhone?: string
      email?: string
    }
    [key: string]: any
  }
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface DealershipBasics {
  dealershipId: string
  configurationData: {
    storeName: string
    oemDesignation?: string
    address?: {
      city?: string
      state?: string
    }
    primaryPhone?: string
    email?: string
    [key: string]: any
  }
}

/** Response from GET /analytics/dealership/:dealershipId/dashboard */
export interface DealershipDashboardStats {
  dateRange: { startDate: string; endDate: string }
  totalLeads: number
  totalConversations: number
  activeConversations: number
  conversations: {
    totalConversations: number
    activeConversations: number
    completedConversations: number
    abandonedConversations: number
    totalMessages: number
    averageMessagesPerConversation: number
    completionRate: number
    averageConversationDuration: number
  }
  pendingAppointments: number
  upcomingAppointments: number
  totalAppointments: number
  showroomVisits: number
  missedAppointments: number
  vehiclesSold: number
  rescheduled: number
  abandoned: number
  showRate: number
  conversationsInternetNew: number
  conversationsFirstContact: number
  totalAppointmentsAi: number
  upcomingAppointmentsAi: number
  showroomVisitsAi: number
  missedAppointmentsAi: number
  vehiclesSoldAi: number
  rescheduledAi: number
  abandonedAi: number
  showRateAi: number
  conversationsCrmStopped: number
  conversationsAbandoned: number
  conversationsEscalated: number
  trends: {
    conversationsGrowth: number
    completionRateChange: number
  }
}

/** Response from GET /analytics/dealership-group/:groupId/dashboard */
export interface GroupDashboardStats {
  totalLeads: number
  totalConversations: number
  activeConversations: number
  conversationsInternetNew: number
  conversationsFirstContact: number
  totalAppointments: number
  upcomingAppointments: number
  showroomVisits: number
  missedAppointments: number
  vehiclesSold: number
  rescheduled: number
  abandoned: number
  showRate: number
  totalAppointmentsAi: number
  upcomingAppointmentsAi: number
  showroomVisitsAi: number
  missedAppointmentsAi: number
  vehiclesSoldAi: number
  rescheduledAi: number
  abandonedAi: number
  showRateAi: number
  conversationsCrmStopped: number
  conversationsAbandoned: number
  conversationsEscalated: number
  dateRange: { startDate: string; endDate: string }
  dealerships: Array<{
    dealershipId: string
    totalConversations: number
    totalAppointments: number
    upcomingAppointments: number
    conversationsInternetNew: number
    conversationsFirstContact: number
    totalAppointmentsAi: number
    upcomingAppointmentsAi: number
  }>
}

/** @deprecated Use GroupDashboardStats; kept for accidental imports */
export type GroupStats = GroupDashboardStats

// ===== DEALERSHIP GROUPS API =====

/**
 * Create a new dealership group
 */
export interface CreateDealershipGroupRequest {
  groupId: string
  configurationData: {
    groupName: string
    groupDescription?: string
    headquarters?: {
      address?: {
        line1?: string
        line2?: string
        city?: string
        state?: string
        zipCode?: string
      }
      primaryPhone?: string
      email?: string
      website?: string
    }
    brandingGuidelines?: {
      logoUrl?: string
      primaryColor?: string
      secondaryColor?: string
    }
    timezone?: string
    [key: string]: any
  }
}

export async function createDealershipGroup(data: CreateDealershipGroupRequest): Promise<DealershipGroupResponse> {
  try {
    const response = await apiClient.post<{ success: boolean; data: DealershipGroupResponse }>('/dealership-groups', data)
    return response.data
  } catch (error: any) {
    console.error('Failed to create dealership group:', error)
    // Rethrow so callers can read error.response.data (e.g. validation errors)
    throw error
  }
}

/**
 * Get all dealership groups
 */
export async function getDealershipGroups(): Promise<DealershipGroupResponse[]> {
  try {
    const response = await apiClient.get<{ success: boolean; data: DealershipGroupResponse[] }>('/dealership-groups')
    return response.data || []
  } catch (error: any) {
    console.error('Failed to fetch dealership groups:', error)
    throw new Error(error.response?.data?.error || error.message || 'Failed to fetch dealership groups')
  }
}

/**
 * Get all dealerships in a specific group
 */
export async function getDealershipsInGroup(groupId: string): Promise<DealershipBasics[]> {
  try {
    const response = await apiClient.get<{ success: boolean; data: DealershipBasics[] }>(
      `/dealership-groups/${groupId}/dealerships`,
    )
    return response.data || []
  } catch (error: any) {
    console.error(`Failed to fetch dealerships for group ${groupId}:`, error)
    throw new Error(error.response?.data?.error || error.message || 'Failed to fetch dealerships')
  }
}

/**
 * Get dashboard stats for a dealership group (optional date range; backend defaults to start of ISO week → now)
 */
export async function getGroupDashboardStats(
  groupId: string,
  startDate?: string,
  endDate?: string,
): Promise<GroupDashboardStats> {
  try {
    const params = new URLSearchParams()
    if (startDate) params.set('startDate', startDate)
    if (endDate) params.set('endDate', endDate)
    const qs = params.toString()
    const url = `/analytics/dealership-group/${groupId}/dashboard${qs ? `?${qs}` : ''}`
    const response = await apiClient.get<GroupDashboardStats>(url)
    return response
  } catch (error: any) {
    console.error(`❌ API Error - Failed to fetch group stats for ${groupId}:`, error)
    throw new Error(error.response?.data?.error || error.message || 'Failed to fetch group stats')
  }
}

/**
 * Get dashboard stats for a dealership
 */
/** Item from GET /appointments/dealership/:dealershipId?startDate=&endDate= */
export interface DealershipAppointmentListItem {
  appointmentId: string
  scheduledAt: string
  status: string
  source?: 'AI_ASSISTANT' | 'CRM_SYNC' | null
  customerId: string
  customerName: string
  customerPhone?: string | null
  customerEmail?: string | null
  leadSource?: string | null
  vehicle?: string | null
  conversationId?: string | null
  messageCount: number
  isSold: boolean
}

export type DealershipAppointmentsTabFilter = 'all' | 'sold' | 'completed' | 'missed'

/** Paginated response from GET /appointments/dealership/:dealershipId */
export interface DealershipAppointmentListPage {
  items: DealershipAppointmentListItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export const DEALERSHIP_APPOINTMENTS_PAGE_SIZE = 20

/** List appointments for a store; date range uses the same scheduled window as analytics */
export async function listDealershipAppointments(
  dealershipId: string,
  startDate: string,
  endDate: string,
  options?: {
    page?: number
    pageSize?: number
    tab?: DealershipAppointmentsTabFilter
    search?: string
  },
): Promise<DealershipAppointmentListPage> {
  const params = new URLSearchParams({ startDate, endDate })
  const page = options?.page ?? 1
  const pageSize = options?.pageSize ?? DEALERSHIP_APPOINTMENTS_PAGE_SIZE
  params.set('page', String(page))
  params.set('pageSize', String(pageSize))
  const tab = options?.tab ?? 'all'
  if (tab !== 'all') params.set('tab', tab)
  if (options?.search?.trim()) params.set('search', options.search.trim())
  return apiClient.get<DealershipAppointmentListPage>(`/appointments/dealership/${dealershipId}?${params.toString()}`)
}

export async function getDealershipDashboardStats(
  dealershipId: string,
  startDate?: string,
  endDate?: string,
): Promise<DealershipDashboardStats> {
  try {
    const params = new URLSearchParams()
    if (startDate) params.set('startDate', startDate)
    if (endDate) params.set('endDate', endDate)
    const qs = params.toString()
    const url = `/analytics/dealership/${dealershipId}/dashboard${qs ? `?${qs}` : ''}`
    const response = await apiClient.get<DealershipDashboardStats>(url)
    return response
  } catch (error: any) {
    console.error(`❌ API Error - Failed to fetch dealership stats for ${dealershipId}:`, error)
    throw new Error(error.response?.data?.error || error.message || 'Failed to fetch dealership stats')
  }
}

/**
 * Create a new dealership
 */
export interface CreateDealershipRequest {
  dealershipId: string
  groupId: string
  configurationData: {
    storeName: string
    oemDesignation?: string
    address: {
      line1: string
      line2?: string
      city: string
      state: string
      zipCode: string
    }
    primaryPhone?: string
    secondaryPhone?: string
    email?: string
    website?: string
    timezone?: string
  }
}

export async function createDealership(data: CreateDealershipRequest): Promise<{ success: boolean; dealershipId: string }> {
  try {
    // Step 1: Create the business_basics configuration for the dealership
    const configResponse = await apiClient.post<{ success: boolean }>(`/dealerships/${data.dealershipId}/configurations`, {
      type: 'business_basics',
      data: data.configurationData,
    })

    if (!configResponse.success) {
      throw new Error('Failed to create dealership configuration')
    }

    // Step 2: Assign the dealership to the group
    await apiClient.post(`/dealership-groups/${data.groupId}/dealerships/${data.dealershipId}`)

    return {
      success: true,
      dealershipId: data.dealershipId,
    }
  } catch (error: any) {
    console.error('Failed to create dealership:', error)
    throw new Error(error.response?.data?.error || error.message || 'Failed to create dealership')
  }
}
